import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.currentTarget;
      const formDataToSend = new FormData(form);
      
      const response = await fetch('https://readdy.ai/api/form/d5sdrdk6pqudvsuu53g0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChatClick = () => {
    const chatButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (chatButton) {
      chatButton.click();
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 bg-gradient-to-br from-purple-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-green-600 to-purple-600 bg-clip-text text-transparent">
            Get In Touch
          </h2>
          <p className="text-base sm:text-xl text-gray-700 max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
            Have a project in mind? Let's work together to create something amazing.
          </p>
          
          <div className="flex items-center justify-center gap-6 mb-8 sm:mb-12">
            <a
              href="https://www.instagram.com/papispaving?igsh=MTdhOHkxNHB4M2pqdQ%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
            >
              <i className="ri-instagram-line text-2xl sm:text-3xl text-white"></i>
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 max-w-2xl mx-auto mb-8 sm:mb-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-gradient-to-r from-green-600 to-purple-600 rounded-full flex-shrink-0">
                <i className="ri-chat-smile-3-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 text-center sm:text-left">Need a Quick Quote?</h3>
            </div>
            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 px-2">
              Chat with our AI assistant for instant answers to your questions and to get a free quote. Available 24/7!
            </p>
            <button
              onClick={handleChatClick}
              className="bg-gradient-to-r from-green-600 to-purple-600 text-white text-base sm:text-lg font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl hover:from-green-700 hover:to-purple-700 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-center"
            >
              <i className="ri-message-3-line text-xl sm:text-2xl"></i>
              Start Chat Now
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-base sm:text-lg text-gray-600">Or send us a detailed message:</p>
          </div>
          <form 
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit} 
            className="bg-white rounded-2xl shadow-xl p-6 sm:p-10"
          >
            <div className="mb-6 sm:mb-8">
              <label htmlFor="name" className="block text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 sm:px-5 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors"
                placeholder="Your name"
              />
            </div>

            <div className="mb-6 sm:mb-8">
              <label htmlFor="email" className="block text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 sm:px-5 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors"
                placeholder="your.email@example.com"
              />
            </div>

            <div className="mb-6 sm:mb-8">
              <label htmlFor="message" className="block text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                maxLength={500}
                className="w-full px-4 sm:px-5 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors resize-none"
                placeholder="Tell me about your project..."
              />
              <p className="text-sm sm:text-base text-gray-500 mt-2">
                {formData.message.length}/500 characters
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 sm:p-5 bg-green-50 border-2 border-green-500 rounded-xl text-green-700 text-base sm:text-lg">
                Thank you! Your message has been sent successfully.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 sm:p-5 bg-red-50 border-2 border-red-500 rounded-xl text-red-700 text-base sm:text-lg">
                Something went wrong. Please try again.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-green-600 to-purple-600 text-white text-lg sm:text-xl font-bold py-4 sm:py-5 px-6 sm:px-8 rounded-xl hover:from-green-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}